import { TeamOutlined } from '@ant-design/icons'
import * as turf from '@turf/turf'
import { formatDistance } from 'date-fns'
import { cs } from 'date-fns/locale'
import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  Point,
} from 'geojson'
import React, {
  ComponentProps,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
  useState,
} from 'react'
import { Popup, useMap } from 'react-map-gl'

import { CadastreMapInsolvencyFilterFormOutput } from '../../../common/cadastreMapInsolvencyTypes'
import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { ZpusobVyuziti } from '../../../common/ruianTypes'
import { useAppSelector } from '../../../redux/hooks'
import { drmPropertyType } from '../../utils/ruianHelpers'
import { useSetPropertyDrawer } from '../../utils/useSetPropertyDrawer'
import { CadastreMapInsolvencyLayers } from './CadastreMapInsolvencyLayers'
import { CadastreMapInsolvencySource } from './CadastreMapInsolvencySource'
import { CadastreMapInsolvencyLayerControl } from './controlls/CadastreMapInsolvencyLayerControl/CadastreMapInsolvencyLayerControl'
import { RuianLayer } from './types/RuianLayers'
import { RuianQuery } from './util/RuianQuery'

export interface CadastreMapInsolvencyDisplayProps {
  setHighlightedFeatures: Dispatch<
    SetStateAction<FeatureCollection<Geometry, GeoJsonProperties>>
  >
}

export const CadastreMapInsolvencyDisplay: FunctionComponent<CadastreMapInsolvencyDisplayProps> =
  props => {
    const [insolvencyEnabled, setInsolvencyEnabled] = useState(true)
    const [insolvencyFilterParams, setInsolvencyFilterParams] = useState<
      CadastreMapInsolvencyFilterFormOutput | undefined
    >(undefined)

    type PopupProps = {
      id: string
      typ: typeof drmPropertyType[keyof typeof drmPropertyType]
      zpusobVyuziti: ZpusobVyuziti
      posledniVkladZapis: string
      pocetVlastniku: number
      insVec: {
        stavRizeni: {
          nazev: string
        }
        posledniZmenaStavu: string
      }
    }
    const [popups, setPopups] = useState<Feature<Point, PopupProps[]>[]>([])

    const setDrawer = useSetPropertyDrawer()

    const token = useAppSelector(state => state.myProfile.token)
    const handlePopupRowOnClick = useCallback(
      async (popupProps: PopupProps) => {
        const { id, typ } = popupProps
        const url = `https://data.regesta.cz/DrmApi/api/v1.0/Nemovitosti/${typ}/${id}/Ruian?access_token=${token}`
        const ruianKod = await fetch(url)
          .then(response => response.json())
          .then(
            data => data?.kod ?? data?.[0]?.kod ?? data?.[0]?.id ?? data?.id
          )
          .catch(() => undefined)
        if (typ === 'Budova') {
          const ruianBudova = await RuianQuery.StavebniObjekt.where(
            `kod = ${ruianKod}`
          )
            .asGeoJson()
            .then(fc => {
              fc.features = fc.features.map(feature => {
                return {
                  ...feature,
                  properties: {
                    ...feature.properties,
                    layerId: RuianLayer.STAVEBNI_OBJEKT,
                    layerName: 'StavebniObjekt',
                  },
                }
              })
              return fc
            })
          props.setHighlightedFeatures(ruianBudova)
        } else if (typ === 'Parcela') {
          const ruianParcela = await RuianQuery.Parcela.where(
            `id = ${ruianKod}`
          )
            .asGeoJson()
            .then(fc => {
              fc.features = fc.features.map(feature => {
                return {
                  ...feature,
                  properties: {
                    ...feature.properties,
                    layerId: RuianLayer.PARCELA,
                    layerName: 'Parcela',
                  },
                }
              })
              return fc
            })
          props.setHighlightedFeatures(ruianParcela)
        } else if (typ === 'Jednotka') {
          setDrawer(ruianKod, PROPERTY_TYPES.UNIT)
        }
      },
      [token, props, setDrawer]
    )

    const map = useMap()
    const handleOnData = useCallback<
      NonNullable<ComponentProps<typeof CadastreMapInsolvencySource>['onData']>
    >(
      data => {
        if (
          !data ||
          !data.features ||
          !map ||
          !map.current ||
          map.current.getZoom() < 13
        ) {
          setPopups([])
          return
        }
        const popups = (data?.features ?? [])
          .filter(feature => feature.properties?.detaily)
          .map(feature => {
            const geometry = feature.geometry as Point
            const properties = feature.properties?.detaily ?? []
            return turf.point(geometry.coordinates, properties)
          })
        setPopups(popups)
      },
      [map]
    )

    return (
      <>
        {popups.map((popup, index) => (
          <Popup
            key={index}
            longitude={popup.geometry.coordinates[0]}
            latitude={popup.geometry.coordinates[1]}
            closeButton={false}
            anchor="bottom-right"
            maxWidth={undefined}
          >
            <div className="space-y-1 -mb-1.5">
              {popup.properties.map((property, index) => (
                <div
                  key={index}
                  onClick={() => handlePopupRowOnClick(property)}
                  className="flex justify-start items-center space-x-2 text-gray-800 text-sm cursor-pointer hover:text-blue-500"
                >
                  <div>{property.pocetVlastniku}</div>
                  <TeamOutlined title="Vlastníci" />
                  <div>{property?.zpusobVyuziti?.nazev ?? property.typ}</div>
                  <div>{property?.insVec?.stavRizeni?.nazev}</div>
                  <div
                    title={new Date(
                      property?.insVec?.posledniZmenaStavu
                    ).toLocaleDateString()}
                  >
                    {formatDistance(
                      new Date(property?.insVec?.posledniZmenaStavu),
                      new Date(),
                      { addSuffix: true, locale: cs }
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Popup>
        ))}
        <CadastreMapInsolvencyLayerControl
          enabled={insolvencyEnabled}
          onEnabledChange={enabled => setInsolvencyEnabled(enabled)}
          onFilterParams={filterParams => {
            setInsolvencyEnabled(true)
            setInsolvencyFilterParams(filterParams)
          }}
        />
        <CadastreMapInsolvencySource
          id="insolvency"
          filterParams={insolvencyFilterParams}
          onData={handleOnData}
        />
        <CadastreMapInsolvencyLayers
          enabled={insolvencyEnabled}
          source="insolvency"
        />
      </>
    )
  }
